// Las versiones que salen del panel de edición del visor —
// ADR-2026-10-06-panel-de-edicion-en-el-visor.
//
// • `guardarRecorte`: el recorte como imagen NUEVA (`POST /v1/images/crop`, sin IA y
//   gratis; el backend la crea ya activa).
// • `activarVersion`: las ediciones con IA (describir, expandir) el backend las guarda con
//   `active = false` —el contrato de n8n: la pantalla de editar las activa al tocar
//   "Guardar"—. En el visor cada versión YA es una creación guardada, así que se activa al
//   llegar; si no, desaparecía de Creaciones al recargar.

import { atlasBackendFetch } from './atlasBackend';
import { supabase } from './supabase';

/** Una versión de la cadena como la manda la barra de Editar: la fila y la instrucción
 *  que la produjo (`null` en la original). */
export interface VersionParaEditar {
  imagenId: string;
  instruccion: string | null;
}

/** Tope por instrucción en el body (el contrato de `versiones` del backend). */
export const MAX_LARGO_INSTRUCCION_DE_VERSION = 300;

/**
 * La cadena que viaja al escritor de edición: las versiones del visor (sin las
 * "mejoradas", que son la misma imagen) hasta la que se está viendo, inclusive, cada una
 * con la ÚLTIMA instrucción de su lista —la que la produjo; las anteriores son de las
 * versiones de antes—. Si la imagen que se ve no está en la columna, es la única
 * (ADR-2026-10-06-editar-con-referencias-y-escritor).
 */
export function cadenaParaEditar(
  versiones: ReadonlyArray<{ imagen: { id: string | number }; instrucciones: readonly string[]; mejorada?: boolean }>,
  imagenId: string,
): VersionParaEditar[] {
  const propias = versiones.filter((v) => !v.mejorada);
  const i = propias.findIndex((v) => String(v.imagen.id) === imagenId);
  if (i === -1) return [{ imagenId, instruccion: null }];
  return propias.slice(0, i + 1).map((v) => {
    const ultima = v.instrucciones[v.instrucciones.length - 1]?.trim() ?? '';
    return {
      imagenId: String(v.imagen.id),
      instruccion: ultima ? ultima.slice(0, MAX_LARGO_INSTRUCCION_DE_VERSION) : null,
    };
  });
}

/** Tope de versiones por pedido (el contrato de `versiones` del backend: `.max(10)`). */
export const MAX_VERSIONES_EN_LA_CADENA = 10;

/**
 * Una cadena más larga que el tope se recorta conservando la ORIGINAL y las últimas: la
 * original es la versión a la que más conviene volver, y las últimas son la actual y su
 * contexto inmediato. Sin esto, la undécima edición seguida rebotaba con un 400.
 */
export function recortarCadena<T>(cadena: readonly T[]): T[] {
  if (cadena.length <= MAX_VERSIONES_EN_LA_CADENA) return [...cadena];
  return [cadena[0], ...cadena.slice(-(MAX_VERSIONES_EN_LA_CADENA - 1))];
}

/**
 * La cadena de la pantalla de Editar: su historial de la sesión hasta la imagen que se
 * está viendo (lo deshecho no está en la imagen y no viaja), cada una con la instrucción
 * que la produjo, recortada al tope
 * (ADR-2026-10-07-la-pantalla-de-editar-adjunta-y-escribe).
 */
export function cadenaDeLaSesion(
  historial: ReadonlyArray<{ id: string; instruction?: string }>,
  indice: number,
): VersionParaEditar[] {
  return recortarCadena(
    historial.slice(0, indice + 1).map((h) => {
      const texto = h.instruction?.trim() ?? '';
      return { imagenId: h.id, instruccion: texto ? texto.slice(0, MAX_LARGO_INSTRUCCION_DE_VERSION) : null };
    }),
  );
}

/**
 * ¿La edición que terminó es de la cadena que el visor tiene abierta? La edición sigue
 * corriendo aunque el visor se cierre (la grilla de quien lo prestó la sigue por
 * `onEmpieza`), así que al terminar puede haber OTRA imagen abierta: entonces la versión
 * nueva entra a la lista pero no reemplaza lo que se ve ni se cuelga de su columna. Es de
 * la cadena abierta cuando la base de la edición y la imagen que se ve están las dos en la
 * columna (las "mejoradas" son la misma imagen y no cuentan). Cerrado el visor, la columna
 * está vacía y nunca lo es — review Greptile de atlas-code#167,
 * ADR-2026-10-08-la-edicion-tardia-no-pisa-otra-imagen.
 */
export function esDeLaCadenaAbierta(
  versiones: ReadonlyArray<{ imagen: { id: string | number }; mejorada?: boolean }>,
  imagenAbiertaId: string | null,
  baseId: string,
): boolean {
  if (!imagenAbiertaId) return false;
  const propias = versiones.filter((v) => !v.mejorada).map((v) => String(v.imagen.id));
  return propias.includes(baseId) && propias.includes(imagenAbiertaId);
}

/** El aviso del visor cuando el escritor editó desde otra versión que la que se veía. */
export function avisoDeBase(baseVersion: number | undefined): string {
  return baseVersion === 1
    ? 'Editamos desde la versión original, para no arrastrar cambios de las anteriores.'
    : `Editamos desde la versión ${baseVersion ?? 'anterior'}, para no arrastrar cambios de las siguientes.`;
}

export interface AreaDeRecorte {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Guarda el recorte como versión nueva y devuelve el id (o file_name) de la fila. */
export async function guardarRecorte(p: {
  imagenId: string;
  /** Fracciones de la imagen (0–1). */
  area: AreaDeRecorte;
  /** `ancho:alto` del recorte. */
  formato?: string;
}): Promise<string> {
  const res = await atlasBackendFetch('/v1/images/crop', {
    imagen_id: p.imagenId,
    area: p.area,
    ...(p.formato ? { formato: p.formato } : {}),
    modo_utilizado: 'recortar_imagen',
  });
  const data = (await res.json().catch(() => null)) as {
    response?: string;
    edited_image_id?: string | number;
    error?: string;
  } | null;
  if (!res.ok || data?.response !== 'success' || !data.edited_image_id) {
    throw new Error(data?.error || 'No pudimos guardar el recorte. Probá de nuevo.');
  }
  return String(data.edited_image_id);
}

/** La deja visible en Creaciones. Falla en silencio: la versión igual se ve en el visor. */
export async function activarVersion(id: string, companyId: string): Promise<void> {
  const { error } = await supabase
    .from('imagenes_generadas')
    .update({ active: true })
    .eq('id', id)
    .eq('id_company', companyId);
  if (error) console.error('No se pudo activar la versión editada:', error);
}

/** `ancho:alto` reducido (1024×1280 → "4:5"); con lados raros quedan las medidas. */
export function formatoDeMedidas(ancho: number, alto: number): string | undefined {
  const w = Math.round(ancho);
  const h = Math.round(alto);
  if (w <= 0 || h <= 0) return undefined;
  const mcd = (a: number, b: number): number => (b === 0 ? a : mcd(b, a % b));
  const d = mcd(w, h);
  return `${w / d}:${h / d}`;
}
